import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FiFilm, FiZap, FiSearch, FiUpload, FiTrash2, FiCheck, FiLoader, FiX, FiType, FiMic, FiVideo, FiPlay, FiStar } from 'react-icons/fi';
import { fullStoryVideoService as api } from '../../services/apiService';
import { useT } from '../../utils/i18n';
import { formatSeconds, showError } from '../FullStory/shared';
import { LookPanel, MusicSelect, FramePicker } from './LookControls';
import { PlateTemplatePicker } from './PlateTemplatePicker';

const KIND_ICON = { voice: FiMic, spot: FiVideo, byte: FiMic, gfx: FiType };
const KIND_STYLE = {
  voice: 'border-blue-200',
  spot: 'border-amber-300 bg-amber-50/40',
  byte: 'border-orange-300 bg-orange-50/40',
  gfx: 'border-red-200 bg-red-50/30',
};
const fmt = (sec) => {
  const s = Math.max(0, Math.round(sec || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/** How long a scene runs (voice: its sentences + pauses; spot: its range; plate: its seconds). */
export const sceneSeconds = (story, scene) => {
  if (scene.kind === 'gfx') return scene.seconds || 3;
  if (scene.kind === 'spot' || scene.kind === 'byte') return Math.max(0, (scene.end || 0) - (scene.start || 0));
  const byId = new Map(story.voice.sentences.map((s) => [String(s._id), s]));
  return (scene.sentenceIds || []).reduce((n, sid) => n + (byId.get(String(sid))?.durationSec || 0) + 0.3, 0);
};

/** Step 3: every scene of the package — footage under the voice-over, spots and bites, text plates. */
export const ScenesStep = ({ story, options, run, readOnly }) => {
  const t = useT();
  const id = story._id;
  const [instructions, setInstructions] = useState(story.images.instructions || '');
  const [picker, setPicker] = useState(null); // scene being given footage
  const [framing, setFraming] = useState(null); // scene being given an overlay frame
  const planning = story.images.status === 'planning' || Boolean(story.images.progress);
  const pendingFootage = (story.sources || []).some((s) => ['uploading', 'queued', 'processing'].includes(s.status));
  const total = story.scenes.reduce((n, s) => n + sceneSeconds(story, s), 0);
  const missing = story.scenes.filter((s) => !s.image?.filename).length;
  const sentenceText = useMemo(() => new Map(story.voice.sentences.map((s) => [String(s._id), s.text])), [story.voice.sentences]);

  const plan = () => {
    if (story.scenes.length && !window.confirm(t('fullStoryVideo.scenes.replanConfirm'))) return;
    run(() => api.planScenes(id, { instructions }));
  };

  return (
    <div className="space-y-4">
      <section className="card space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="text-base font-semibold text-gray-900">{t('fullStoryVideo.scenes.title')}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{t('fullStoryVideo.scenes.hint')}</p>
          </div>
          {story.scenes.length > 0 && (
            <p className={`text-sm tabular-nums ${total > 1800 ? 'text-danger-600 font-semibold' : 'text-gray-600'}`}>
              {t('fullStoryVideo.scenes.runningTime', { time: formatSeconds(total), count: story.scenes.length })}
            </p>
          )}
        </div>
        {!readOnly && (
          <>
            <textarea value={instructions} onChange={(e) => setInstructions(e.target.value.slice(0, 500))} rows={2}
              placeholder={t('fullStoryVideo.scenes.instructions')} className="input w-full text-sm" disabled={planning} />
            <div className="flex flex-wrap items-center gap-2">
              <button className="btn btn-primary text-sm flex items-center gap-1.5 disabled:opacity-50" onClick={plan} disabled={planning || pendingFootage}>
                <FiZap size={14} /> {story.scenes.length ? t('fullStoryVideo.scenes.replan') : t('fullStoryVideo.scenes.plan')}
              </button>
              {pendingFootage && <span className="text-xs text-amber-700">{t('fullStoryVideo.scenes.waitFootage')}</span>}
            </div>
          </>
        )}
        {planning && (
          <p className="text-sm text-primary-700 flex items-center gap-2"><FiLoader className="animate-spin" size={14} /> {story.images.progress || t('fullStoryVideo.scenes.planning')}</p>
        )}
        {story.images.status === 'failed' && story.images.error && <p className="text-sm text-danger-600">{story.images.error}</p>}
      </section>

      {story.scenes.length > 0 && <LookPanel story={story} options={options} run={run} readOnly={readOnly} t={t} />}

      {story.scenes.length > 0 && (
        <ol className="space-y-2">
          {story.scenes.map((scene, i) => {
            const firstOfBlock = i === 0 || story.scenes[i - 1].blockIndex !== scene.blockIndex;
            const block = story.script.blocks[scene.blockIndex];
            return (
              <React.Fragment key={scene._id}>
                {firstOfBlock && block && (
                  <li className="pt-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                    {t(`fullStoryVideo.block.${block.type}`)}{block.note ? ` — ${block.note}` : ''}
                  </li>
                )}
                <SceneRow story={story} scene={scene} index={i} text={scene.kind === 'voice' ? (scene.sentenceIds || []).map((sid) => sentenceText.get(String(sid))).join(' ') : scene.text}
                  readOnly={readOnly} run={run} onPick={() => setPicker(scene)} onFrame={() => setFraming(scene)} options={options} t={t} />
              </React.Fragment>
            );
          })}
        </ol>
      )}

      {story.scenes.length > 0 && !readOnly && (
        <div className="card flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-gray-600">{missing ? t('fullStoryVideo.scenes.missing', { count: missing }) : t('fullStoryVideo.scenes.allReady')}</p>
          <button className="btn btn-primary text-sm flex items-center gap-1.5 disabled:opacity-50" disabled={planning || missing > 0 || total > 1800}
            onClick={() => run(() => api.approveImages(id))}>
            <FiCheck size={14} /> {t('fullStoryVideo.scenes.approve')}
          </button>
        </div>
      )}

      {picker && <FootagePicker story={story} scene={picker} onClose={() => setPicker(null)} run={run} t={t} />}
      {framing && (
        <FramePicker story={story} scene={framing} options={options} run={run} onClose={() => setFraming(null)} t={t}
          thumb={framing.image ? api.fileUrl(id, framing.image.kind === 'video' ? framing.image.poster : framing.image.filename) : null} />
      )}
    </div>
  );
};

const SceneRow = ({ story, scene, index, text, readOnly, run, onPick, onFrame, options, t }) => {
  const [choosingTemplate, setChoosingTemplate] = useState(false);
  const id = story._id;
  const [panel, setPanel] = useState(null); // 'ai' | 'web' | 'plate'
  const [brief, setBrief] = useState(scene.prompt || '');
  const [query, setQuery] = useState(scene.photoQuery || '');
  const [plateText, setPlateText] = useState(scene.text || '');
  const [plateSeconds, setPlateSeconds] = useState(String(scene.seconds || 3));
  const fileRef = useRef(null);
  const Icon = KIND_ICON[scene.kind] || FiFilm;
  const media = scene.image;
  const source = media?.sourceId && (story.sources || []).find((s) => String(s._id) === String(media.sourceId));
  const thumb = media ? (media.kind === 'video' ? media.poster : media.filename) : null;
  const seconds = sceneSeconds(story, scene);
  const frameLabel = scene.frame?.id ? (options?.frames || []).find((f) => f.id === scene.frame.id)?.label || scene.frame.id : '';

  let origin = '';
  if (media?.source === 'footage') {
    origin = scene.kind === 'spot' || scene.kind === 'byte'
      ? t('fullStoryVideo.scene.fromRange', { name: source?.originalName || '', from: fmt(scene.start), to: fmt(scene.end) })
      : t('fullStoryVideo.scene.fromFootage', { name: source?.originalName || '', at: fmt(media.clipStart) });
  } else if (media?.source) origin = t(`fullStoryVideo.scene.source.${media.source}`);

  return (
    <li className={`rounded-xl border bg-white p-2.5 ${KIND_STYLE[scene.kind] || ''}`}>
      <div className="flex gap-3">
        <div className="relative w-36 sm:w-44 aspect-video rounded-lg bg-gray-100 overflow-hidden flex-shrink-0">
          {thumb && <img src={api.fileUrl(id, thumb)} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />}
          {scene.status === 'generating' && <div className="absolute inset-0 flex items-center justify-center bg-white/70"><FiLoader className="animate-spin text-primary-600" /></div>}
          {!thumb && scene.status !== 'generating' && <div className="absolute inset-0 flex items-center justify-center text-gray-400"><Icon size={18} /></div>}
          <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1 text-[10px] text-white tabular-nums">{index + 1} · {formatSeconds(seconds)}</span>
          {frameLabel && <span className="absolute top-1 right-1 rounded bg-amber-500/90 px-1 text-[10px] text-white flex items-center gap-0.5" title={frameLabel}><FiStar size={9} /> {frameLabel}</span>}
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-sm text-gray-800 break-words">{text || <span className="text-gray-400">{t(`fullStoryVideo.block.${scene.kind}`)}</span>}</p>
          {origin && <p className="text-xs text-gray-500 truncate" title={scene.reason || ''}>{origin}</p>}
          {scene.error && <p className="text-xs text-danger-600">{scene.error}</p>}
          <MusicSelect story={story} scene={scene} options={options} run={run} readOnly={readOnly} t={t} />
          {!readOnly && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              <button className="btn btn-ghost text-xs py-1 px-2 flex items-center gap-1" onClick={onPick}>
                <FiFilm size={12} /> {scene.kind === 'gfx' ? t('fullStoryVideo.scene.platePhoto') : t('fullStoryVideo.scene.chooseFootage')}
              </button>
              {scene.kind === 'voice' && (
                <>
                  <button className="btn btn-ghost text-xs py-1 px-2 flex items-center gap-1" onClick={() => setPanel(panel === 'ai' ? null : 'ai')}><FiZap size={12} /> {t('fullStoryVideo.scene.aiPicture')}</button>
                  <button className="btn btn-ghost text-xs py-1 px-2 flex items-center gap-1" onClick={() => setPanel(panel === 'web' ? null : 'web')}><FiSearch size={12} /> {t('fullStoryVideo.scene.webPhoto')}</button>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files[0]; e.target.value = ''; if (f) run(() => api.uploadSceneImage(id, scene._id, f)); }} />
                  <button className="btn btn-ghost text-xs py-1 px-2 flex items-center gap-1" onClick={() => fileRef.current?.click()}><FiUpload size={12} /> {t('fullStoryVideo.scene.upload')}</button>
                </>
              )}
              {scene.kind === 'gfx' && (
                <>
                  <button className="btn btn-ghost text-xs py-1 px-2 flex items-center gap-1" onClick={() => setPanel(panel === 'plate' ? null : 'plate')}><FiType size={12} /> {t('fullStoryVideo.scene.editPlate')}</button>
                  {story.plateTemplatesOn && (
                    <button className="btn btn-ghost text-xs py-1 px-2 flex items-center gap-1" onClick={() => setChoosingTemplate(true)}>
                      <FiStar size={12} /> {scene.plateTemplate ? t('fullStoryVideo.scene.plateOwn') : t('fullStoryVideo.scene.plateChange')}
                    </button>
                  )}
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files[0]; e.target.value = ''; if (f) run(() => api.uploadSceneImage(id, scene._id, f)); }} />
                  <button className="btn btn-ghost text-xs py-1 px-2 flex items-center gap-1" onClick={() => fileRef.current?.click()}><FiUpload size={12} /> {t('fullStoryVideo.scene.platePhotoUpload')}</button>
                </>
              )}
              {media && (
                <button className="btn btn-ghost text-xs py-1 px-2 flex items-center gap-1" onClick={onFrame}><FiStar size={12} /> {scene.frame?.id ? t('fullStoryVideo.frame.change') : t('fullStoryVideo.frame.add')}</button>
              )}
              {scene.kind !== 'voice' && (
                <button className="btn btn-ghost text-xs py-1 px-2 flex items-center gap-1 text-danger-600"
                  onClick={() => { if (window.confirm(t('fullStoryVideo.scene.removeConfirm'))) run(() => api.removeScene(id, scene._id)); }}>
                  <FiTrash2 size={12} /> {t('fullStory.delete')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {choosingTemplate && (
        <PlateTemplatePicker
          title={t('fullStoryVideo.scene.plateTitle')}
          hint={t('fullStoryVideo.scene.plateHint')}
          current={scene.plateTemplate || story.plateTemplateUsed}
          onPick={async (templateId) => { const next = await run(() => api.updateScene(story._id, scene._id, { plateTemplate: templateId === story.plateTemplateUsed ? '' : templateId })); if (next) setChoosingTemplate(false); }}
          onClose={() => setChoosingTemplate(false)}
          extra={scene.plateTemplate ? (
            <button className="btn btn-ghost text-sm" onClick={async () => { const next = await run(() => api.updateScene(story._id, scene._id, { plateTemplate: '' })); if (next) setChoosingTemplate(false); }}>
              {t('fullStoryVideo.scene.plateUseStory')}
            </button>
          ) : null}
        />
      )}
      {panel === 'ai' && (
        <div className="mt-2 space-y-2">
          <textarea value={brief} onChange={(e) => setBrief(e.target.value)} rows={3} className="input w-full text-xs" placeholder={t('fullStoryVideo.scene.briefPlaceholder')} />
          <button className="btn btn-primary text-xs py-1.5" onClick={() => { setPanel(null); run(() => api.generateSceneImage(id, scene._id, { prompt: brief })); }}>{t('fullStoryVideo.scene.makePicture')}</button>
        </div>
      )}
      {panel === 'web' && (
        <div className="mt-2 space-y-2">
          <div className="flex gap-2">
            <input value={query} onChange={(e) => setQuery(e.target.value)} className="input flex-1 text-xs" placeholder={t('fullStoryVideo.scene.searchPlaceholder')} />
            <button className="btn btn-primary text-xs py-1.5" onClick={() => run(() => api.searchScenePhotos(id, scene._id, query))}>{t('fullStoryVideo.scene.search')}</button>
          </div>
          {scene.webOptions?.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5">
              {scene.webOptions.map((o) => (
                <button key={o.filename} className="relative aspect-video rounded overflow-hidden border border-gray-200 hover:ring-2 hover:ring-primary-400"
                  onClick={() => { setPanel(null); run(() => api.chooseScenePhoto(id, scene._id, o.filename)); }}>
                  <img src={api.fileUrl(id, o.filename)} alt={o.title || ''} className="absolute inset-0 w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {panel === 'plate' && (
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <input value={plateText} onChange={(e) => setPlateText(e.target.value.slice(0, 200))} className="input flex-1 min-w-[200px] text-sm" />
          <label className="text-xs text-gray-600 flex items-center gap-1">
            {t('fullStoryVideo.scene.seconds')}
            <input type="number" min={1} max={15} step={0.5} value={plateSeconds} onChange={(e) => setPlateSeconds(e.target.value)} className="input w-16 text-sm py-1" />
          </label>
          <button className="btn btn-primary text-xs py-1.5" onClick={() => { setPanel(null); run(() => api.updateScene(id, scene._id, { text: plateText, seconds: Number(plateSeconds) })); }}>{t('fullStory.saveEdits')}</button>
        </div>
      )}
    </li>
  );
};

/**
 * Choose the moment of footage for a scene: pick a video, click a moment (or
 * play to the point and use the current time); spots and bites also set
 * where the footage stops.
 */
const FootagePicker = ({ story, scene, onClose, run, t }) => {
  const id = story._id;
  const ranged = scene.kind === 'spot' || scene.kind === 'byte';
  const ready = (story.sources || []).filter((s) => s.status === 'ready' && (!ranged || (s.kind === 'video' && s.hasAudio)));
  const initial = ready.find((s) => String(s._id) === String(scene.image?.sourceId || scene.sourceId)) || ready[0];
  const [sourceId, setSourceId] = useState(initial?._id || null);
  const [detail, setDetail] = useState(null);
  const [start, setStart] = useState(scene.start ?? scene.image?.clipStart ?? 0);
  const [end, setEnd] = useState(scene.end ?? ((scene.start ?? 0) + (scene.kind === 'byte' ? 15 : 8)));
  const [filter, setFilter] = useState('');
  const videoRef = useRef(null);

  useEffect(() => {
    if (!sourceId) return;
    setDetail(null);
    api.sourceDetail(id, sourceId).then(setDetail).catch((e) => showError(e));
  }, [id, sourceId]);

  const seek = (sec) => {
    const v = videoRef.current;
    if (v) { v.currentTime = sec; v.play().catch(() => {}); }
  };
  const choose = async () => {
    const next = await run(() => api.chooseFootage(id, scene._id, ranged ? { sourceId, start: Number(start), end: Number(end) } : { sourceId, start: Number(start) }));
    if (next) onClose();
  };
  const shots = (detail?.shots || []).filter((s) => !filter.trim() || `${s.description} ${s.speech || ''}`.toLowerCase().includes(filter.trim().toLowerCase()));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4" onClick={onClose}>
      <div className="w-full max-w-5xl max-h-[94vh] overflow-hidden flex flex-col bg-white rounded-2xl shadow-xl" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">{ranged ? t('fullStoryVideo.picker.titleRange') : t('fullStoryVideo.picker.title')}</h3>
          <button onClick={onClose} className="p-1.5 rounded text-gray-400 hover:bg-gray-100" aria-label={t('prompts.close')}><FiX size={18} /></button>
        </div>
        {!ready.length ? (
          <p className="p-6 text-sm text-gray-500">{ranged ? t('fullStoryVideo.picker.noSoundFootage') : t('fullStoryVideo.picker.noFootage')}</p>
        ) : (
          <div className="flex-1 overflow-y-auto p-4 grid lg:grid-cols-5 gap-4">
            <div className="lg:col-span-3 space-y-3">
              <select value={sourceId || ''} onChange={(e) => setSourceId(e.target.value)} className="input w-full text-sm">
                {ready.map((s) => <option key={s._id} value={s._id}>{s.originalName}{s.kind === 'video' ? ` (${formatSeconds(s.durationSec)})` : ''}</option>)}
              </select>
              {detail && detail.kind === 'video' && (
                <video ref={videoRef} key={detail._id} src={api.fileUrl(id, detail.filename)} poster={detail.poster ? api.fileUrl(id, detail.poster) : undefined}
                  controls preload="metadata" className="w-full rounded-lg bg-black aspect-video"
                  onLoadedMetadata={(e) => { e.currentTarget.currentTime = Number(start) || 0; }} />
              )}
              {detail && detail.kind === 'image' && <img src={api.fileUrl(id, detail.filename)} alt="" className="w-full rounded-lg" />}
              {detail?.kind === 'video' && (
                <div className="flex flex-wrap items-end gap-2 text-sm">
                  <label className="flex flex-col text-xs text-gray-600">{t('fullStoryVideo.picker.start')}
                    <input type="number" step={0.1} min={0} value={start} onChange={(e) => setStart(e.target.value)} className="input w-24 text-sm py-1" />
                  </label>
                  <button className="btn btn-ghost text-xs py-1.5" onClick={() => setStart((videoRef.current?.currentTime || 0).toFixed(1))}>{t('fullStoryVideo.picker.useNowStart')}</button>
                  {ranged && (
                    <>
                      <label className="flex flex-col text-xs text-gray-600">{t('fullStoryVideo.picker.end')}
                        <input type="number" step={0.1} min={0} value={end} onChange={(e) => setEnd(e.target.value)} className="input w-24 text-sm py-1" />
                      </label>
                      <button className="btn btn-ghost text-xs py-1.5" onClick={() => setEnd((videoRef.current?.currentTime || 0).toFixed(1))}>{t('fullStoryVideo.picker.useNowEnd')}</button>
                      <span className="text-xs text-gray-500 tabular-nums">{t('fullStoryVideo.picker.length', { seconds: Math.max(0, Number(end) - Number(start)).toFixed(1) })}</span>
                    </>
                  )}
                  <button className="btn btn-ghost text-xs py-1.5 flex items-center gap-1" onClick={() => seek(Number(start) || 0)}><FiPlay size={11} /> {t('fullStoryVideo.picker.play')}</button>
                </div>
              )}
              <div className="flex justify-end">
                <button className="btn btn-primary text-sm" onClick={choose} disabled={!sourceId}>{t('fullStoryVideo.picker.use')}</button>
              </div>
            </div>
            <div className="lg:col-span-2 space-y-2">
              <input value={filter} onChange={(e) => setFilter(e.target.value)} className="input w-full text-sm" placeholder={t('fullStoryVideo.picker.search')} />
              {!detail ? <p className="text-xs text-gray-400 flex items-center gap-1"><FiLoader className="animate-spin" size={12} /> {t('fullStory.loading')}</p> : (
                <ul className="space-y-1.5 max-h-[60vh] overflow-y-auto pr-1">
                  {shots.map((s) => (
                    <li key={s._id}>
                      <button className={`w-full flex gap-2 text-left rounded-lg border p-1.5 hover:border-primary-300 ${Math.abs(Number(start) - s.start) < 0.05 ? 'border-primary-400 bg-primary-50' : 'border-gray-200'}`}
                        onClick={() => {
                          setStart(s.start);
                          if (ranged) setEnd(Math.min(detail.durationSec || s.end, s.start + Math.max(Number(end) - Number(start), 4)));
                          seek(s.start);
                        }}>
                        {s.keyframe && <img src={api.fileUrl(id, s.keyframe)} alt="" className="w-20 aspect-video object-cover rounded flex-shrink-0" loading="lazy" />}
                        <span className="min-w-0 text-[11px] text-gray-700">
                          {detail.kind === 'video' && <span className="font-semibold tabular-nums">{fmt(s.start)} </span>}
                          {s.description}
                          {s.speech && <span className="block text-gray-500 truncate">“{s.speech}”</span>}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScenesStep;
